import { EChatMessageSource, IChatMessage } from '@adaptiva/core/chat';
import { AdaLocalizedDatePipe, isEmptyInput, isNullOrUndefined, MapValuePipe, provideMergedMap } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { IMarkdownParseResult, MarkdownService } from '@adaptiva/core/markdown';
import { MenuController } from '@adaptiva/core/menus';
import { OverlayService } from '@adaptiva/core/overlays';
import { Thread, ThreadingNotSupportedError } from '@adaptiva/core/threading';
import { awaitElement } from '@adaptiva/core/ui';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { ClickableComponentModule } from '@adaptiva/widgets/clickable';
import { IconComponent } from '@adaptiva/widgets/icon';
import { InlineMenuComponent } from '@adaptiva/widgets/inline-menu';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, computed, contentChild, effect, EffectRef, ElementRef, forwardRef, inject, Injector, input, InputSignal, OnInit, output, OutputEmitterRef, signal, Signal, Type, untracked, viewChild, viewChildren, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { from } from 'rxjs';
import { BaseBubbleComponent } from './classes';
import { ChatBubbleComponent, EllipsisComponent, SuggestedPromptsComponent } from './components';
import { EChatBubbleType } from './enums';
import { BUBBLE_REGISTRATIONS } from './tokens';

const WIDGETS: Type<any>[] = [
	ClickableComponentModule,
	EllipsisComponent,
	IconComponent,
	InlineMenuComponent
];

@Component({
	selector: 'ada-chat',
	templateUrl: './chat.component.html',
	styleUrls: ['./chat.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		WidgetService,
		provideMergedMap(BUBBLE_REGISTRATIONS, [
			[EChatBubbleType.TEXT, ChatBubbleComponent]
		]),
		{
			provide: BaseWidgetComponent,
			useExisting: forwardRef(() => ChatComponent)
		}
	],
	imports: [
		AdaLocalizedDatePipe,
		MapValuePipe,
		CommonModule,
		SuggestedPromptsComponent,
		FormsModule,
		WIDGETS,
	],
})
export class ChatComponent extends BaseWidgetComponent implements OnInit {

	protected readonly clearOnSubmit: InputSignal<boolean> = input(true);

	protected readonly inputPlaceholder: InputSignal<string> = input('Type a message...');

	protected readonly sendButtonText: InputSignal<string> = input('Send');

	protected readonly isResponding: InputSignal<boolean> = input(false);

	protected readonly isFooterVisible: InputSignal<boolean> = input(true);

	protected readonly isSendAllowed: InputSignal<boolean> = input(true);

	protected readonly messages: InputSignal<IChatMessage[]> = input([]);

	protected readonly showTranscript: InputSignal<boolean> = input(true);

	protected readonly suggestions: InputSignal<string[]> = input([]);

	protected readonly activeIndex: InputSignal<int | null> = input(null);

	protected readonly queryController: InputSignal<MenuController<any>> = input();

	protected readonly responseController: InputSignal<MenuController<any>> = input();

	protected readonly characterLimit: InputSignal<int> = input(0);

	protected readonly warningThreshold: InputSignal<int> = input(null);

	protected readonly messageSent: OutputEmitterRef<string> = output();

	protected readonly chatInput: Signal<ElementRef<HTMLInputElement>> = viewChild.required('ChatInput');

	protected readonly chatTranscript: Signal<ElementRef<HTMLElement>> = viewChild.required('ChatTranscript');

	protected readonly dynamicElements: Signal<readonly NgComponentOutlet[]> = viewChildren(NgComponentOutlet);

	protected readonly projectedFooter: Signal<ElementRef<any>> = contentChild('ChatFooter');

	protected markdownThread!: Thread<IMarkdownParseResult>;

	protected readonly injector: Injector = inject(Injector);

	protected readonly logger: LoggingService = inject(LoggingService);

	protected readonly markdownService: MarkdownService = inject(MarkdownService);

	protected readonly overlayService: OverlayService = inject(OverlayService);

	protected readonly registrations: Map<string, Type<BaseBubbleComponent>> = inject(BUBBLE_REGISTRATIONS);

	protected readonly currentMessage: WritableSignal<string> = signal('');

	protected readonly sendDisabled: Signal<boolean> = computed(() => !this.isSendAllowed() || this.isResponding() || this.isOverLimit() || isEmptyInput(this.currentMessage()));

	protected readonly messageLength: Signal<int> = computed(() => {
		return this.currentMessage()?.length;
	});

	protected readonly shouldShowWarning: Signal<boolean> = computed(() => {
		let _displayLimit: int = this.characterLimit();

		if (this.warningThreshold())
			_displayLimit *= this.warningThreshold();

		return !!this.characterLimit() && (this.messageLength() > _displayLimit);
	});

	protected readonly isOverLimit: Signal<boolean> = computed(() => {
		return !!this.characterLimit() && (this.messageLength() > this.characterLimit());
	});

	protected readonly inputWarning: Signal<string> = computed(() => {
		return this.isOverLimit() ? 'Message exceeds character limit' : 'Nearing character limit';
	});

	protected readonly isChatEditable: Signal<string> = computed(() => {
		return this.isSendAllowed() ? 'plaintext-only' : 'false';
	});

	protected readonly chatBubbles: Signal<BaseBubbleComponent[]> = computed(() => {
		const _dynamicElements: readonly NgComponentOutlet[] = this.dynamicElements();

		return _dynamicElements.map(c => c.componentInstance).filter(c => c instanceof BaseBubbleComponent);
	});

	protected readonly focusFx: EffectRef = effect(() => {
		const _index: int | null = this.activeIndex();
		const _messages: IChatMessage[] = untracked(() => this.messages());

		if (isNullOrUndefined(_index) || _index > _messages.length)
			return;

		from(awaitElement('.ada-chat-bubble.ada-is-active p')).pipe(this.takeOneUntilDestroyed()).subscribe({
			next: () => untracked(() => this.chatBubbles())?.[_index]?.scrollIntoView()
		});
	});

	protected readonly chatEffect: EffectRef = effect(() => {
		if (this.showTranscript()) {
			this.chatTranscript();
			this.messages();

			if (this.isChatAtBottom())
				afterNextRender(this.scrollChatToBottom.bind(this), { injector: this.injector });
		}
	});

	protected readonly responseType: EChatMessageSource = EChatMessageSource.RESPONSE;

	public override ngOnInit(): void {
		super.ngOnInit();

		try {
			this.markdownThread = this.markdownService.allocateThread();
		} catch (e) {
			if (e instanceof ThreadingNotSupportedError) {
				this.overlayService.error(
					'Multi-threading Unavailable',
					'This browser does not support multi-threading through Web Workers, which is required to render the chat experience as intended. ' +
					'Please install a more up-to-date (preferably Chromium) browser to use this feature.'
				);
			} else {
				this.overlayService.error(
					'Multi-threading Failed',
					this.logger.extractErrorMessage(e)
				);
			}
		}
	}

	protected onInput(_event: InputEvent): void {
		const _target: EventTarget = _event.currentTarget;

		if (_target instanceof HTMLElement) {
			const _nextMessage: string = _target.innerText.trim();

			this.currentMessage.set(_nextMessage);

			// content editable divs cleared by user aren't counted as "empty" in css	
			if (!_nextMessage)
				this.updateMessage('');
		}
	}

	protected onPaste(_event: ClipboardEvent): void {
		_event.preventDefault();
		const _text: string = _event.clipboardData.getData('text/plain');

		this.updateMessage(_text);
	}

	public isChatAtBottom(): boolean {
		const _element: HTMLElement = this.chatTranscript().nativeElement;
		const _bottomThresholdPx: int = 10;

		return _element.scrollTop >= (_element.scrollHeight - _element.offsetHeight - _bottomThresholdPx);
	}

	public scrollChatToBottom(): void {
		const _element: HTMLElement = this.chatTranscript().nativeElement;

		_element.scrollTo({
			top: _element.scrollHeight,
			behavior: 'smooth',
		});
	}

	public sendMessage(): void {
		const _message: string = this.currentMessage().trim();

		if (!_message || this.sendDisabled())
			return;

		this.messageSent.emit(_message);
		if (this.clearOnSubmit())
			this.updateMessage('');

		this.scrollChatToBottom();
	}

	public sendMessageOnEnter(_event: KeyboardEvent): void {
		if (_event?.key?.toLowerCase() === 'enter' && !_event?.shiftKey) {
			_event.preventDefault();
			this.sendMessage();

			const _target: EventTarget = _event.currentTarget;

			if (_target instanceof HTMLElement)
				_target.blur();
		}
	}

	protected updateMessage(_text: string): void {
		this.currentMessage.set(_text);
		this.chatInput().nativeElement.innerText = _text;
	}

}
